import { gql } from '@apollo/client';

import {
  BAGGING_DISPATCH_FIELDS,
  BAGGING_DISPATCH_RECORD_FIELDS,
  CHEF_FIELDS,
  COMPLAINT_FIELDS,
  COMPONENT_BATCH_FIELDS,
  DELETE_COMPONENT_BATCH_RESPONSE_FIELDS,
  DELIVERY_TICKET_FIELDS,
  DRIVER_DISPATCH_FIELDS,
  EXPORT_DOCUMENTS_RESPONSE_FIELDS,
  EXPORT_FILE_RESPONSE_FIELDS,
  PROD_CONTROL_MEAL_FIELDS,
  FOOD_COMPONENT_FIELDS,
  FOOD_COMPONENT_YIELD_BATCH_FIELDS,
  FOOD_FIELDS,
  FOOD_PORTIONING_RECORD_FIELDS,
  HOURLY_DATA_UPDATE_EVENT_FIELDS,
  KDSKITCHEN_FIELDS,
  MEAL_TIMESTAMP_RECORD_FIELDS,
  NOTIFICATION_STATS_FIELDS
} from './fragments';
import { withoutCostFields } from './withoutCosts';

export const ADD_MEAL_TIMESTAMP_MUTATION = gql`
  ${MEAL_TIMESTAMP_RECORD_FIELDS}
  mutation AddMealTimestamp($input: AddMealTimestampInput!) {
    addMealTimestamp(input: $input) {
      ...MealTimestampRecordFields
    }
  }
`;

export const APPROVE_DELIVERY_TICKET_MUTATION = gql`
  ${DELIVERY_TICKET_FIELDS}
  mutation ApproveDeliveryTicket($id: String, $type: DeliveryTicketType, $kitchen: Kitchen, $user: UserInput) {
    approveDeliveryTicket(id: $id, type: $type, kitchen: $kitchen, user: $user) {
      ...DeliveryTicketFields
    }
  }
`;

export const APPROVE_DELIVERY_TICKET_V2_MUTATION = gql`
  ${DELIVERY_TICKET_FIELDS}
  mutation ApproveDeliveryTicketV2($id: String!, $type: DeliveryTicketType, $kitchen: Kitchen, $user: UserInput) {
    approveDeliveryTicketV2(id: $id, type: $type, kitchen: $kitchen, user: $user) {
      ...DeliveryTicketFields
    }
  }
`;

export const UPDATE_FOOD_PORTIONING_RECORD_MUTATION = gql`
  ${FOOD_PORTIONING_RECORD_FIELDS}
  mutation UpdateFoodPortioningRecord($input: UpdateFoodPortioningRecordInput!) {
    updateFoodPortioningRecord(input: $input) {
      ...FoodPortioningRecordFields
    }
  }
`;

export const FINALIZE_FOOD_PORTIONING_RECORD_MUTATION = gql`
  ${FOOD_PORTIONING_RECORD_FIELDS}
  mutation FinalizeFoodPortioningRecord(
    $kitchen: Kitchen!
    $day: String!
    $shift: Session!
    $variant: FoodPortioningRecordVariant!
    $user: UserInput!
  ) {
    finalizeFoodPortioningRecord(kitchen: $kitchen, day: $day, shift: $shift, variant: $variant, user: $user) {
      ...FoodPortioningRecordFields
    }
  }
`;

export const UPDATE_BAGGING_DISPATCH_RECORD_MUTATION = gql`
  ${BAGGING_DISPATCH_RECORD_FIELDS}
  mutation UpdateBaggingDispatchRecord($input: UpdateBaggingDispatchRecordInput!) {
    updateBaggingDispatchRecord(input: $input) {
      ...BaggingDispatchRecordFields
    }
  }
`;

export const FINALIZE_BAGGING_DISPATCH_RECORD_MUTATION = gql`
  ${BAGGING_DISPATCH_RECORD_FIELDS}
  mutation FinalizeBaggingDispatchRecord($kitchen: Kitchen!, $day: String!, $shift: Session!, $user: UserInput!) {
    finalizeBaggingDispatchRecord(kitchen: $kitchen, day: $day, shift: $shift, user: $user) {
      ...BaggingDispatchRecordFields
    }
  }
`;

export const BATCH_CREATE_COMPONENT_SHORTAGE_MUTATION = withoutCostFields(gql`
  ${FOOD_COMPONENT_FIELDS}
  mutation BatchCreateComponentShortage($input: [ComponentShortageInput], $kitchen: Kitchen, $shift: String) {
    batchCreateComponentShortage(input: $input, kitchen: $kitchen, shift: $shift) {
      ...FoodComponentFields
    }
  }
`);

export const BATCH_CREATE_COMPONENT_SHORTAGE_V2_MUTATION = withoutCostFields(gql`
  ${FOOD_COMPONENT_FIELDS}
  mutation BatchCreateComponentShortageV2($input: [ComponentShortageInput]!, $kitchen: Kitchen, $shift: String) {
    batchCreateComponentShortageV2(input: $input, kitchen: $kitchen, shift: $shift) {
      ...FoodComponentFields
    }
  }
`);

export const BATCH_UPDATE_FOODS_PORTIONING_STATUS_MUTATION = withoutCostFields(gql`
  ${FOOD_FIELDS}
  mutation BatchUpdateFoodsPortioningStatus($kitchen: Kitchen, $foodsIds: [String]) {
    batchUpdateFoodsPortioningStatus(kitchen: $kitchen, foodsIds: $foodsIds) {
      ...FoodFields
    }
  }
`);

export const BATCH_UPDATE_FOODS_PORTIONING_STATUS_V2_MUTATION = withoutCostFields(gql`
  ${FOOD_FIELDS}
  mutation BatchUpdateFoodsPortioningStatusV2($kitchen: Kitchen!, $foodsIds: [String]!) {
    batchUpdateFoodsPortioningStatusV2(kitchen: $kitchen, foodsIds: $foodsIds) {
      ...FoodFields
    }
  }
`);

export const CREATE_CHEF_MUTATION = gql`
  ${CHEF_FIELDS}
  mutation CreateChef($data: ChefInput) {
    createChef(data: $data) {
      ...ChefFields
    }
  }
`;

export const CREATE_CHEF_V2_MUTATION = gql`
  ${CHEF_FIELDS}
  mutation CreateChefV2($data: ChefInput!) {
    createChefV2(data: $data) {
      ...ChefFields
    }
  }
`;

export const CREATE_COMPONENT_BATCH_MUTATION = gql`
  ${COMPONENT_BATCH_FIELDS}
  mutation CreateComponentBatch($data: ComponentBatchInput) {
    createComponentBatch(data: $data) {
      ...ComponentBatchFields
    }
  }
`;

export const CREATE_COMPONENT_BATCH_V2_MUTATION = gql`
  ${COMPONENT_BATCH_FIELDS}
  mutation CreateComponentBatchV2($data: ComponentBatchInput!) {
    createComponentBatchV2(data: $data) {
      ...ComponentBatchFields
    }
  }
`;

export const CREATE_FOOD_COMPONENT_YIELD_BATCH_MUTATION = gql`
  ${FOOD_COMPONENT_YIELD_BATCH_FIELDS}
  mutation CreateFoodComponentYieldBatch($data: CreateFoodComponentYieldBatchInput!, $user: UserInput!) {
    createFoodComponentYieldBatch(data: $data, user: $user) {
      ...FoodComponentYieldBatchFields
    }
  }
`;

export const DELETE_CHEF_MUTATION = gql`
  ${CHEF_FIELDS}
  mutation DeleteChef($id: String!, $kitchen: Kitchen!) {
    deleteChef(id: $id, kitchen: $kitchen) {
      ...ChefFields
    }
  }
`;

export const DELETE_COMPONENT_BATCH_MUTATION = gql`
  ${DELETE_COMPONENT_BATCH_RESPONSE_FIELDS}
  mutation DeleteComponentBatch($id: String, $kitchen: String) {
    deleteComponentBatch(id: $id, kitchen: $kitchen) {
      ...DeleteComponentBatchResponseFields
    }
  }
`;

export const DELETE_COMPONENT_BATCH_V2_MUTATION = gql`
  ${DELETE_COMPONENT_BATCH_RESPONSE_FIELDS}
  mutation DeleteComponentBatchV2($id: String!, $kitchen: String) {
    deleteComponentBatchV2(id: $id, kitchen: $kitchen) {
      ...DeleteComponentBatchResponseFields
    }
  }
`;

export const DELIVERY_TICKET_CREATED_MUTATION = gql`
  ${DELIVERY_TICKET_FIELDS}
  mutation DeliveryTicketCreated($id: String, $kitchen: Kitchen) {
    deliveryTicketCreated(id: $id, kitchen: $kitchen) {
      ...DeliveryTicketFields
    }
  }
`;

export const EXPORT_COMPLAINT_LIST_MUTATION = gql`
  ${EXPORT_FILE_RESPONSE_FIELDS}
  mutation ExportComplaintList($filters: ComplaintsFiltersInput) {
    exportComplaintList(filters: $filters) {
      ...ExportFileResponseFields
    }
  }
`;

export const EXPORT_COMPONENT_BATCH_MUTATION = gql`
  ${EXPORT_FILE_RESPONSE_FIELDS}
  mutation ExportComponentBatch($kitchen: Kitchen, $date: RangeInput, $shift: String) {
    exportComponentBatch(kitchen: $kitchen, date: $date, shift: $shift) {
      ...ExportFileResponseFields
    }
  }
`;

export const EXPORT_DELIVERY_TICKET_MEAL_STATS_REPORT_MUTATION = gql`
  ${EXPORT_FILE_RESPONSE_FIELDS}
  mutation ExportDeliveryTicketMealStatsReport($kitchen: Kitchen!, $date: String!) {
    exportDeliveryTicketMealStatsReport(kitchen: $kitchen, date: $date) {
      ...ExportFileResponseFields
    }
  }
`;

export const EXPORT_DOCUMENTS_MUTATION = gql`
  ${EXPORT_DOCUMENTS_RESPONSE_FIELDS}
  mutation ExportDocuments($ids: [String]!, $docTypes: [DocsType]!, $day: String, $kitchen: Kitchen, $shift: String) {
    exportDocuments(ids: $ids, docTypes: $docTypes, day: $day, kitchen: $kitchen, shift: $shift) {
      ...ExportDocumentsResponseFields
    }
  }
`;

export const EXPORT_FOOD_COMPONENT_SHORTAGE_MUTATION = gql`
  ${EXPORT_FILE_RESPONSE_FIELDS}
  mutation ExportFoodComponentShortage($kitchen: Kitchen, $startDate: String, $endDate: String) {
    exportFoodComponentShortage(kitchen: $kitchen, startDate: $startDate, endDate: $endDate) {
      ...ExportFileResponseFields
    }
  }
`;

export const EXPORT_FOOD_COMPONENT_STATUS_MUTATION = gql`
  ${EXPORT_FILE_RESPONSE_FIELDS}
  mutation ExportFoodComponentStatus($kitchen: Kitchen, $startDate: String, $endDate: String) {
    exportFoodComponentStatus(kitchen: $kitchen, startDate: $startDate, endDate: $endDate) {
      ...ExportFileResponseFields
    }
  }
`;

export const EXPORT_FOOD_COMPONENT_WASTAGE_MUTATION = gql`
  ${EXPORT_FILE_RESPONSE_FIELDS}
  mutation ExportFoodComponentWastage($kitchen: Kitchen, $startDate: String, $endDate: String) {
    exportFoodComponentWastage(kitchen: $kitchen, startDate: $startDate, endDate: $endDate) {
      ...ExportFileResponseFields
    }
  }
`;

export const EXPORT_FOOD_LABELS_MUTATION = gql`
  ${EXPORT_DOCUMENTS_RESPONSE_FIELDS}
  mutation ExportFoodLabels($input: ExportFoodLabelsReq) {
    exportFoodLabels(input: $input) {
      ...ExportDocumentsResponseFields
    }
  }
`;

export const EXPORT_FOOD_PORTIONING_STATUS_MUTATION = gql`
  ${EXPORT_FILE_RESPONSE_FIELDS}
  mutation ExportFoodPortioningStatus($kitchen: Kitchen, $startDate: String, $endDate: String, $showCustom: Boolean) {
    exportFoodPortioningStatus(kitchen: $kitchen, startDate: $startDate, endDate: $endDate, showCustom: $showCustom) {
      ...ExportFileResponseFields
    }
  }
`;

export const EXPORT_MEAL_INVOICES_MUTATION = gql`
  ${EXPORT_DOCUMENTS_RESPONSE_FIELDS}
  mutation ExportMealInvoices($id: String, $kitchen: Kitchen, $shoppingMenuFoodSlug: String, $email: String) {
    exportMealInvoices(id: $id, kitchen: $kitchen, shoppingMenuFoodSlug: $shoppingMenuFoodSlug, email: $email) {
      ...ExportDocumentsResponseFields
    }
  }
`;

export const EXPORT_QUALITY_METRICS_MUTATION = gql`
  ${EXPORT_FILE_RESPONSE_FIELDS}
  mutation ExportQualityMetrics(
    $email: String
    $startDate: String
    $endDate: String
    $kitchen: Kitchen
    $shift: Session
    $type: ExportMetricsTypeEnum
    $stations: [CookingStationName]
  ) {
    exportQualityMetrics(
      email: $email
      startDate: $startDate
      endDate: $endDate
      kitchen: $kitchen
      shift: $shift
      type: $type
      stations: $stations
    ) {
      ...ExportFileResponseFields
    }
  }
`;

export const GET_IMAGE_UPLOAD_URL_MUTATION = gql`
  ${EXPORT_FILE_RESPONSE_FIELDS}
  mutation GetImageUploadURL($path: String) {
    getImageUploadURL(path: $path) {
      ...ExportFileResponseFields
    }
  }
`;

export const HOURLY_DATA_UPDATED_MUTATION = gql`
  ${HOURLY_DATA_UPDATE_EVENT_FIELDS}
  mutation HourlyDataUpdated($kitchen: Kitchen!, $date: String!, $updatedAt: String!) {
    hourlyDataUpdated(kitchen: $kitchen, date: $date, updatedAt: $updatedAt) {
      ...HourlyDataUpdateEventFields
    }
  }
`;

export const MODIFY_BAGGING_DISPATCH_MUTATION = gql`
  ${BAGGING_DISPATCH_FIELDS}
  mutation ModifyBaggingDispatch($id: String, $input: BaggingDispatchInput!) {
    modifyBaggingDispatch(id: $id, input: $input) {
      ...BaggingDispatchFields
    }
  }
`;

export const REJECT_DELIVERY_TICKET_MUTATION = gql`
  ${DELIVERY_TICKET_FIELDS}
  mutation RejectDeliveryTicket($id: String, $type: DeliveryTicketType, $kitchen: Kitchen, $user: UserInput) {
    rejectDeliveryTicket(id: $id, type: $type, kitchen: $kitchen, user: $user) {
      ...DeliveryTicketFields
    }
  }
`;

export const REJECT_DELIVERY_TICKET_V2_MUTATION = gql`
  ${DELIVERY_TICKET_FIELDS}
  mutation RejectDeliveryTicketV2($id: String!, $type: DeliveryTicketType, $kitchen: Kitchen, $user: UserInput) {
    rejectDeliveryTicketV2(id: $id, type: $type, kitchen: $kitchen, user: $user) {
      ...DeliveryTicketFields
    }
  }
`;

export const RESOLVE_COMPLAINT_MUTATION = gql`
  ${COMPLAINT_FIELDS}
  mutation ResolveComplaint($id: String, $status: ComplaintStatus, $kitchen: Kitchen, $user: UserInput) {
    resolveComplaint(id: $id, status: $status, kitchen: $kitchen, user: $user) {
      ...ComplaintFields
    }
  }
`;

export const RESOLVE_COMPLAINT_V2_MUTATION = gql`
  ${COMPLAINT_FIELDS}
  mutation ResolveComplaintV2($id: String!, $status: ComplaintStatus, $kitchen: Kitchen, $user: UserInput) {
    resolveComplaintV2(id: $id, status: $status, kitchen: $kitchen, user: $user) {
      ...ComplaintFields
    }
  }
`;

export const UPDATE_ACTUAL_COOKED_WEIGHT_MUTATION = withoutCostFields(gql`
  ${FOOD_COMPONENT_FIELDS}
  mutation UpdateActualCookedWeight($kitchen: Kitchen, $id: String, $actualCookedWeight: [ActualCookedWeightEntryInput]) {
    updateActualCookedWeight(kitchen: $kitchen, id: $id, actualCookedWeight: $actualCookedWeight) {
      ...FoodComponentFields
    }
  }
`);

export const UPDATE_ACTUAL_COOKED_WEIGHT_V2_MUTATION = withoutCostFields(gql`
  ${FOOD_COMPONENT_FIELDS}
  mutation UpdateActualCookedWeightV2($kitchen: Kitchen!, $id: String, $actualCookedWeight: [ActualCookedWeightEntryInput!]!) {
    updateActualCookedWeightV2(kitchen: $kitchen, id: $id, actualCookedWeight: $actualCookedWeight) {
      ...FoodComponentFields
    }
  }
`);

export const UPDATE_CHEF_MUTATION = gql`
  ${CHEF_FIELDS}
  mutation UpdateChef($id: String, $input: ChefInput) {
    updateChef(id: $id, input: $input) {
      ...ChefFields
    }
  }
`;

export const UPDATE_CHEF_V2_MUTATION = gql`
  ${CHEF_FIELDS}
  mutation UpdateChefV2($id: String!, $input: ChefInput!) {
    updateChefV2(id: $id, input: $input) {
      ...ChefFields
    }
  }
`;

export const UPDATE_COMPONENT_BATCH_MUTATION = gql`
  ${COMPONENT_BATCH_FIELDS}
  mutation UpdateComponentBatch($id: String, $input: ComponentBatchInput) {
    updateComponentBatch(id: $id, input: $input) {
      ...ComponentBatchFields
    }
  }
`;

export const UPDATE_COMPONENT_BATCH_V2_MUTATION = gql`
  ${COMPONENT_BATCH_FIELDS}
  mutation UpdateComponentBatchV2($id: String!, $input: ComponentBatchInput!) {
    updateComponentBatchV2(id: $id, input: $input) {
      ...ComponentBatchFields
    }
  }
`;

export const UPDATE_DELIVERY_TICKETS_DOWNLOADED_AT_MUTATION = gql`
  ${DELIVERY_TICKET_FIELDS}
  mutation UpdateDeliveryTicketsDownloadedAt($ids: [String]!, $kitchen: Kitchen!) {
    updateDeliveryTicketsDownloadedAt(ids: $ids, kitchen: $kitchen) {
      ...DeliveryTicketFields
    }
  }
`;

export const UPDATE_DRIVER_DISPATCH_MUTATION = gql`
  ${DRIVER_DISPATCH_FIELDS}
  mutation UpdateDriverDispatch($id: String, $input: DriverDispatchInput) {
    updateDriverDispatch(id: $id, input: $input) {
      ...DriverDispatchFields
    }
  }
`;

export const UPDATE_DRIVER_DISPATCH_V2_MUTATION = gql`
  ${DRIVER_DISPATCH_FIELDS}
  mutation UpdateDriverDispatchV2($id: String!, $input: DriverDispatchInput!) {
    updateDriverDispatchV2(id: $id, input: $input) {
      ...DriverDispatchFields
    }
  }
`;

export const UPDATE_EXTRA_MEALS_MUTATION = withoutCostFields(gql`
  ${FOOD_FIELDS}
  mutation UpdateExtraMeals($kitchen: Kitchen, $data: [UpdateExtraMealsInput]) {
    updateExtraMeals(kitchen: $kitchen, data: $data) {
      ...FoodFields
    }
  }
`);

export const UPDATE_EXTRA_MEALS_V2_MUTATION = withoutCostFields(gql`
  ${FOOD_FIELDS}
  mutation UpdateExtraMealsV2($kitchen: Kitchen!, $data: [UpdateExtraMealsInput]!) {
    updateExtraMealsV2(kitchen: $kitchen, data: $data) {
      ...FoodFields
    }
  }
`);

export const UPDATE_FCWASTAGE_INPUTS_MUTATION = withoutCostFields(gql`
  ${FOOD_COMPONENT_FIELDS}
  mutation UpdateFCWastageInputs($kitchen: Kitchen, $date: String, $id: String, $inputs: FCWastageInputs!) {
    updateFCWastageInputs(kitchen: $kitchen, date: $date, id: $id, inputs: $inputs) {
      ...FoodComponentFields
    }
  }
`);

export const UPDATE_FOOD_COMPONENT_STATUS_MUTATION = withoutCostFields(gql`
  ${FOOD_COMPONENT_FIELDS}
  mutation UpdateFoodComponentStatus(
    $kitchen: Kitchen!
    $id: String!
    $station: CookingStationName!
    $operation: FoodComponentStationOperation!
    $shortagesIds: [String]
    $shift: String
  ) {
    updateFoodComponentStatus(
      kitchen: $kitchen
      id: $id
      station: $station
      operation: $operation
      shortagesIds: $shortagesIds
      shift: $shift
    ) {
      ...FoodComponentFields
    }
  }
`);

export const UPDATE_FOOD_COMPONENT_YIELD_BATCH_MUTATION = gql`
  ${FOOD_COMPONENT_YIELD_BATCH_FIELDS}
  mutation UpdateFoodComponentYieldBatch($kitchen: Kitchen!, $id: String!, $data: UpdateFoodComponentYieldBatchInput!) {
    updateFoodComponentYieldBatch(kitchen: $kitchen, id: $id, data: $data) {
      ...FoodComponentYieldBatchFields
    }
  }
`;

export const UPDATE_FOOD_COMPONENT_YIELD_BATCH_TOTAL_WEIGHTS_MUTATION = gql`
  ${FOOD_COMPONENT_YIELD_BATCH_FIELDS}
  mutation UpdateFoodComponentYieldBatchTotalWeights($data: UpdateFoodComponentYieldBatchTotalWeightsInput!) {
    updateFoodComponentYieldBatchTotalWeights(data: $data) {
      ...FoodComponentYieldBatchFields
    }
  }
`;

export const UPDATE_FOOD_PORTIONING_STATUS_MUTATION = withoutCostFields(gql`
  ${FOOD_FIELDS}
  mutation UpdateFoodPortioningStatus(
    $kitchen: Kitchen!
    $id: String!
    $session: String
    $operation: UpdateFoodPortioningStatusOperation!
    $type: FoodPortioningStatusType!
    $totalProcessedMealCount: Int
    $totalSelectedMealCount: Int
  ) {
    updateFoodPortioningStatus(
      kitchen: $kitchen
      id: $id
      session: $session
      operation: $operation
      type: $type
      totalProcessedMealCount: $totalProcessedMealCount
      totalSelectedMealCount: $totalSelectedMealCount
    ) {
      ...FoodFields
    }
  }
`);

export const UPDATE_KITCHEN_BUFFERS_MUTATION = gql`
  ${KDSKITCHEN_FIELDS}
  mutation UpdateKitchenBuffers($kitchen: Kitchen!, $componentWeightBuffers: [ComponentWeightBufferInput]!) {
    updateKitchenBuffers(kitchen: $kitchen, componentWeightBuffers: $componentWeightBuffers) {
      ...KDSKitchenFields
    }
  }
`;

export const UPDATE_NOTIFICATION_STATS_MUTATION = gql`
  ${NOTIFICATION_STATS_FIELDS}
  mutation UpdateNotificationStats($date: String, $kitchen: Kitchen, $updates: [NotificationUpdateInput]) {
    updateNotificationStats(date: $date, kitchen: $kitchen, updates: $updates) {
      ...NotificationStatsFields
    }
  }
`;

export const VERIFY_COMPONENT_MUTATION = withoutCostFields(gql`
  ${FOOD_COMPONENT_FIELDS}
  mutation VerifyComponent($input: VerifyComponentInput, $kitchen: Kitchen) {
    verifyComponent(input: $input, kitchen: $kitchen) {
      ...FoodComponentFields
    }
  }
`);

export const VERIFY_COMPONENT_V2_MUTATION = withoutCostFields(gql`
  ${FOOD_COMPONENT_FIELDS}
  mutation VerifyComponentV2($input: VerifyComponentInput!, $kitchen: Kitchen) {
    verifyComponentV2(input: $input, kitchen: $kitchen) {
      ...FoodComponentFields
    }
  }
`);

export const UPDATE_PROD_CONTROL_MEAL_MUTATION = gql`
  ${PROD_CONTROL_MEAL_FIELDS}
  mutation UpdateProdControlMeal(
    $kitchen: Kitchen!
    $day: String!
    $foodId: String!
    $size: String!
    $delta: Int!
    $actor: String!
    $shift: String
  ) {
    updateProdControlMeal(
      kitchen: $kitchen
      day: $day
      foodId: $foodId
      size: $size
      delta: $delta
      actor: $actor
      shift: $shift
    ) {
      ...ProdControlMealFields
    }
  }
`;

export const SET_PROD_CONTROL_MEAL_MUTATION = gql`
  ${PROD_CONTROL_MEAL_FIELDS}
  mutation SetProdControlMeal(
    $kitchen: Kitchen!
    $day: String!
    $foodId: String!
    $size: String!
    $quantity: Int!
    $expectedQuantity: Int
    $actor: String!
    $shift: String
  ) {
    setProdControlMeal(
      kitchen: $kitchen
      day: $day
      foodId: $foodId
      size: $size
      quantity: $quantity
      expectedQuantity: $expectedQuantity
      actor: $actor
      shift: $shift
    ) {
      ...ProdControlMealFields
    }
  }
`;

export const TRIGGER_TOP_CONTROL_SYNC_MUTATION = gql`
  mutation TriggerTopControlSync($kitchen: Kitchen, $day: String, $url: String, $email: String) {
    triggerTopControlSync(kitchen: $kitchen, day: $day, url: $url, email: $email) {
      message
    }
  }
`;
